// Parse Cyrqle RES (pipe-delimited) content into structured biomarkers,
// and categorize by body system for display.

// RES field indices (per RFD):
// 0-4: Patient IDs / Name fields
// 5: Test code
// 6: Name
// 7: Normal flag (N = normal)
// 9: Value
// 11: Unit
// 12-14: Reference range parts (min/max/range string)

const CATEGORY_RULES = [
  {
    key: "hematology",
    match: /HEMATOCRIT|HEMOGLOB|HEMATIES|LEUCOCITOS|PLAQUETAS|VCM|HCM|CHCM|VSG|RDW|NEUTRO|LINFOC|MONOC|EOSINO|BASOFIL|RETICULO/i,
  },
  {
    key: "lipids",
    match: /COLESTEROL|HDL|LDL|TRIGLIC|TRIGLYC|APO|VLDL/i,
  },
  {
    key: "glucose",
    match: /GLUCOS|HBA1C|HEMOGLOBINA GLIC|INSULIN/i,
  },
  {
    key: "liver",
    match: /GOT|GPT|ALT|AST|GGT|FOSFATAS|BILIRRUB|ALBUMIN|PROTEIN|LDH/i,
  },
  {
    key: "kidney",
    match: /UREA|CREATININ|FILTRADO|ACIDO URIC|URICO|MICROALB/i,
  },
  {
    key: "minerals",
    match: /CALCIO|MAGNES|ZINC|HIERRO|FERRITIN|TRANSFERR|FOSFOR|SODIO|POTASIO|CLORURO|COBRE/i,
  },
  {
    key: "vitamins",
    match: /VITAMIN|FOLATO|FOLICO|B12|D3|D 25|HIDROXIVITAMIN/i,
  },
  {
    key: "thyroid",
    match: /TSH|T3|T4|TIROID|TIROXIN/i,
  },
];

export function categorize(name = "") {
  const upper = name.toUpperCase();
  for (const r of CATEGORY_RULES) {
    if (r.match.test(upper)) return r.key;
  }
  return "other";
}

function toNumber(v) {
  if (v == null) return null;
  const s = String(v).replace(",", ".").trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

export function parseResContent(resContent = "") {
  if (!resContent) return { patient: null, biomarkers: [] };
  const lines = resContent.split(/\r?\n/).filter(Boolean);
  const biomarkers = [];
  let patient = null;

  for (const line of lines) {
    const cols = line.split("|");
    if (cols.length < 8) continue;

    if (!patient && (cols[3] || cols[4])) {
      patient = {
        firstName: cols[3] || "",
        lastName: cols[4] || "",
        fullName: `${cols[3] || ""} ${cols[4] || ""}`.trim(),
        recordNumber: cols[0] || cols[2] || "",
      };
    }

    const code = cols[5] || "";
    const name = (cols[6] || "").trim();
    if (!name) continue;
    const flag = (cols[7] || "").trim().toUpperCase();
    const isNormal = flag === "N" || flag === "" ? flag === "N" : false;
    const value = (cols[9] || "").trim();
    const unit = (cols[11] || "").trim();
    const refMin = toNumber(cols[12]);
    const refMax = toNumber(cols[13]);
    const refRange = (cols[14] || "").trim();
    const numericValue = toNumber(value);

    biomarkers.push({
      code,
      name,
      value,
      numericValue,
      unit,
      flag,
      isNormal,
      referenceMin: refMin,
      referenceMax: refMax,
      referenceRange: refRange || (refMin != null && refMax != null ? `${refMin} - ${refMax}` : ""),
      category: categorize(name),
    });
  }

  return { patient, biomarkers };
}

export function deriveFlag(b) {
  // Returns "low" | "high" | "normal" | "unknown"
  if (b.isNormal) return "normal";
  if (b.numericValue == null) return b.flag ? "abnormal" : "unknown";
  if (b.referenceMin != null && b.numericValue < b.referenceMin) return "low";
  if (b.referenceMax != null && b.numericValue > b.referenceMax) return "high";
  return b.flag ? "abnormal" : "normal";
}

export function aggregateBiomarkers(biomarkers = []) {
  const total = biomarkers.length;
  const normal = biomarkers.filter((b) => b.isNormal).length;
  const altered = total - normal;
  const pctNormal = total > 0 ? Math.round((normal / total) * 100) : 0;
  return { total, normal, altered, pctNormal };
}

export function pickKeyFindings(parsed, algorithmResult, max = 3) {
  // Returns abstract findings; UI can translate "tone" → human hint.
  const findings = [];
  const altered = (parsed.biomarkers || []).filter((b) => !b.isNormal).slice(0, max);
  altered.forEach((b) => {
    const dir = deriveFlag(b);
    findings.push({
      title: b.name,
      value: `${b.value}${b.unit ? " " + b.unit : ""}`,
      direction: dir, // "high" | "low" | "abnormal" | "normal" | "unknown"
      tone: "alert",
    });
  });
  if (findings.length < max && algorithmResult) {
    for (const key of Object.keys(algorithmResult)) {
      if (findings.length >= max) break;
      const algo = algorithmResult[key];
      if (algo && algo.description) {
        findings.push({
          title: key,
          value: algo.response || "",
          description: algo.description,
          tone: "info",
        });
      }
    }
  }
  return findings.slice(0, max);
}
